import React, { useContext, useState, useEffect } from 'react';
import { CircularProgress } from '@mui/material';
import { GoogleMap, LoadScriptNext, MarkerF } from '@react-google-maps/api'; // Use LoadScriptNext
import AuthContext from '../../../context/AuthContext';
import DrawerContext from '../../../context/DrawerContext';
import ModalContext from '../../../context/ModalContext';
import TextInput from '../../../common/TextInput';

const mapContainerStyle = {
  width: "100%",
  height: "400px",
};

const DefaultLocation = {
  lat: -33.9249,
  lng: 18.4241,
};

const DefaultZoom = 10;

// Max lengths to avoid ER_DATA_TOO_LONG (must not exceed DB column sizes)
const LOCATION_MAX_LENGTH = 255;
const AFFILIATE_CODE_MAX_LENGTH = 20;

const AddAgent = () => {

  const authCtx = useContext(AuthContext);
  const drawerCtx = useContext(DrawerContext);
  const modalCtx = useContext(ModalContext);
  const [loading, setLoading] = useState(false);
  const [address, setAddress] = useState("");
  const [selectedLocation, setSelectedLocation] = useState(DefaultLocation);
  const [agentObj, setAgentObj] = useState({ first_name: "", last_name: "", email: "", phone: "", status: "", location: "" , coords:"", affiliate_code: ""});
  const [originalData, setOriginalData] = useState(null);
  const [userId, setUserId] = useState(null); // Store userId for API call when adding from modal
  const [affiliateCodeError, setAffiliateCodeError] = useState("");
  const isEditMode = drawerCtx.details && drawerCtx.details.id;
  const isFromModal = drawerCtx.details && drawerCtx.details.fromModal;

  // Generate affiliate code based on name
  const generateAffiliateCode = (firstName, lastName) => {
    const first = (firstName || "").toUpperCase().replace(/[^A-Z]/g, "").substring(0, 3);
    const last = (lastName || "").toUpperCase().replace(/[^A-Z]/g, "").substring(0, 3);
    const randomNum = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
    return `${first}${last}${randomNum}`.toUpperCase();
  };

  useEffect(() => {
    if (isEditMode) {
      const { firstName, lastName, email, phone, status, location, latitude, longitude, affiliateCode} = drawerCtx.details;
      const originalObj = { first_name: firstName, last_name: lastName, email, phone, status, location, coords: `${latitude}, ${longitude}`, affiliate_code: affiliateCode || "" };
      setAgentObj(originalObj);
      setOriginalData(originalObj);
      setAddress(location || "");

      if (latitude && longitude) {
        const lat = parseFloat(latitude);
        const lng = parseFloat(longitude);
  
        // Update map and coordinates field
        setSelectedLocation({ lat, lng });
        setAgentObj((prev) => ({ ...prev, coords: `${lat}, ${lng}` }));
      }
    } else if (isFromModal) {
      // Handle data from modal - pre-fill name and email, make them non-editable
      const { userId: modalUserId, firstName, lastName, email, phone } = drawerCtx.details;
      setUserId(modalUserId); // Store userId for API call
      const generatedCode = generateAffiliateCode(firstName, lastName);
      const modalObj = { 
        first_name: firstName || "", 
        last_name: lastName || "", 
        email: email || "", 
        phone: phone || "", 
        status: "", 
        location: "", 
        coords: "",
        affiliate_code: generatedCode
      };
      setAgentObj(modalObj);
    }
  }, [drawerCtx.details, isEditMode, isFromModal]);

  // Auto-generate affiliate code when name is entered in add mode (not from modal)
  useEffect(() => {
    if (!isEditMode && !isFromModal && agentObj.first_name && agentObj.last_name && !agentObj.affiliate_code) {
      const generatedCode = generateAffiliateCode(agentObj.first_name, agentObj.last_name);
      setAgentObj((prev) => ({ ...prev, affiliate_code: generatedCode }));
    }
  }, [agentObj.first_name, agentObj.last_name, isEditMode, isFromModal]);

  // Fetch address 
  const fetchAddress = async (lat, lng) => {
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`
      );
      const data = await response.json();
  
      if (data.status === "OK" && data.results.length > 0) {
        const formattedAddress = (data.results[0].formatted_address || "").slice(0, LOCATION_MAX_LENGTH);
        setAddress(formattedAddress);
        setAgentObj((prev) => ({ ...prev, location: formattedAddress }));
        return formattedAddress;
      } else {
        setAddress("Address not found");
        return null;
      }
    } catch (error) {
      console.error("Error fetching address:", error);
      setAddress("Error fetching address");
      return null;
    }
  };

  const handleMapClick = async(e) => {
    const { latLng } = e;
    const lat = latLng.lat();
    const lng = latLng.lng();

    setSelectedLocation({ lat, lng });
    fetchAddress(lat, lng);
    setAgentObj({ ...agentObj, location: `${lat}, ${lng}`, coords: `${lat}, ${lng}`});
  };

  // Validate affiliate code uniqueness (debounced) - only when user manually changes it
  useEffect(() => {
    const affiliateCode = agentObj.affiliate_code?.trim();
    
    // Skip validation if code is empty or unchanged from original (in edit mode)
    if (!affiliateCode || (isEditMode && affiliateCode === originalData?.affiliate_code)) {
      setAffiliateCodeError("");
      return;
    }

    // Debounce validation
    const timeoutId = setTimeout(async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URI}/v3/admin/agent/check-affiliate?code=${encodeURIComponent(affiliateCode)}${isEditMode ? `&excludeId=${drawerCtx.details.id}` : ''}`,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${authCtx.token}`
            }
          }
        );
        const data = await response.json();
        
        if (data.status === 'success') {
          if (data.data?.isUnique === false) {
            setAffiliateCodeError("This affiliate code is already taken. Please choose another.");
          } else {
            setAffiliateCodeError("");
          }
        } else {
          setAffiliateCodeError("Error checking code availability.");
        }
      } catch (error) {
        console.error('Error validating affiliate code:', error);
        setAffiliateCodeError("Error checking code availability.");
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [agentObj.affiliate_code, isEditMode, originalData?.affiliate_code, drawerCtx.details?.id, authCtx.token]);

  const hasChanges = () => {
    if (!originalData) return false;
    // In edit mode, check location and affiliate code
    if (isEditMode) {
      return agentObj.location !== originalData.location || agentObj.coords !== originalData.coords || agentObj.affiliate_code !== originalData.affiliate_code || address !== originalData.location;
    }
    return JSON.stringify(agentObj) !== JSON.stringify(originalData);
  };

  const submitAgent = async () => {
    if (isEditMode && !hasChanges()) {
      return;
    }

    setLoading(true);
    const url = isEditMode
      ? `${process.env.REACT_APP_API_URI}/v3/admin/agent/${drawerCtx.details.id}` //Edit
      : `${process.env.REACT_APP_API_URI}/v3/admin/agent`; //Add

    const method = isEditMode ? 'PUT' : 'POST';

    // Validate affiliate code before submitting
    if (!agentObj.affiliate_code?.trim()) {
      setAffiliateCodeError("Affiliate code is required.");
      setLoading(false);
      return;
    }

    if (affiliateCodeError) {
      setLoading(false);
      return;
    }

    // Truncate to DB column limits to avoid ER_DATA_TOO_LONG
    const safeLocation = (address || "").trim().slice(0, LOCATION_MAX_LENGTH);
    const safeAffiliateCode = (agentObj.affiliate_code || "").trim().slice(0, AFFILIATE_CODE_MAX_LENGTH);

    // Prepare request body based on mode
    let requestBody;
    if (isEditMode) {
      // Edit mode: send location and affiliate code (only if changed)
      requestBody = {
        location: safeLocation,
        latitude: selectedLocation.lat,
        longitude: selectedLocation.lng
      };
      
      // Only include affiliateCode if it has changed from the original
      if (safeAffiliateCode !== (originalData?.affiliate_code || "").trim()) {
        requestBody.affiliateCode = safeAffiliateCode;
      }
    } else {
      // Add mode: send userId, location, latitude, longitude, affiliateCode, status
      requestBody = {
        userId: userId,
        location: safeLocation,
        latitude: selectedLocation.lat,
        longitude: selectedLocation.lng,
        affiliateCode: safeAffiliateCode,
        status: "active"
      };
    }

    try {
      const response = await fetch(url, {
        method: method,
        mode: 'cors',
        headers: { 'Content-Type': 'application/json', Authorization: "Bearer " + authCtx.token },
        body: JSON.stringify(requestBody)
      });
      const data = await response.json();
      if (data.status === "success") {
        setLoading(false);
        drawerCtx.closeDrawer("add-agent");
        drawerCtx.closeDrawer("edit-agent");
        // Trigger page update to refresh agents list
        modalCtx.setPageUpdate({ page: "Agents" });
      } else {
        // Handle API errors
        const errorMessage = data.message || "";
        if (errorMessage.toLowerCase().includes('unique') ||
            errorMessage.toLowerCase().includes('duplicate') ||
            errorMessage.toLowerCase().includes('already exists')) {
          setAffiliateCodeError("This affiliate code is already taken. Please choose another.");
        } else if (errorMessage.toLowerCase().includes('data too long') || errorMessage.includes('ER_DATA_TOO_LONG')) {
          alert("One or more fields are too long. Please shorten the address or affiliate code and try again.");
        } else {
          alert(errorMessage || "Failed to save agent.");
        }
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      console.error('Error:', error);
      alert("An error occurred while saving the agent.");
    }
  };

  return (
    <div className="drawer-container">
      <div className="drawer">
        <header>
          <h1>{isEditMode ? "Edit Agent" : "Add Agent"}</h1>
        </header>
        <section style={{ padding: '1rem' }}>
          {(isFromModal || isEditMode) ? (
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
              <div style={{ flex: 1 }}>
                <div className="form-label" style={{ fontWeight: 'bold' }}>Name</div>
                <div style={{ padding: '10px 0', fontSize: '1rem', color: '#333' }}>
                  {`${agentObj.first_name || ''} ${agentObj.last_name || ''}`.trim() || 'N/A'}
                </div>
              </div>
              <div style={{ flex: 1 }}>
                <div className="form-label" style={{ fontWeight: 'bold' }}>Email</div>
                <div style={{ padding: '10px 0', fontSize: '1rem', color: '#333' }}>
                  {agentObj.email || 'N/A'}
                </div>
              </div>
              <div style={{ flex: 1 }}>
                <div className="form-label" style={{ fontWeight: 'bold' }}>Phone</div>
                <div style={{ padding: '10px 0', fontSize: '1rem', color: '#333' }}>
                  {agentObj.phone || 'N/A'}
                </div>
              </div>
            </div>
          ) : (
            <>
              <TextInput name="first_name" label="First Name" value={agentObj.first_name} onChange={(e) => setAgentObj({ ...agentObj, first_name: e.target.value })} placeholder="First Name" />
              <TextInput name="last_name" label="Last Name" value={agentObj.last_name} onChange={(e) => setAgentObj({ ...agentObj, last_name: e.target.value })} placeholder="Last Name" />
              <TextInput name="email" label="Email" value={agentObj.email} onChange={(e) => setAgentObj({ ...agentObj, email: e.target.value })} placeholder="Email" />
              <TextInput name="phone" label="Phone" value={agentObj.phone} onChange={(e) => setAgentObj({ ...agentObj, phone: e.target.value })} placeholder="Phone" />
            </>
          )}
          <div style={{ position: 'relative' }}>
            <TextInput 
              name="affiliate_code" 
              label="Affiliate Code" 
              value={agentObj.affiliate_code} 
              maxLength={AFFILIATE_CODE_MAX_LENGTH}
              onChange={(e) => {
                const code = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, AFFILIATE_CODE_MAX_LENGTH);
                setAgentObj({ ...agentObj, affiliate_code: code });
                setAffiliateCodeError(""); // Clear error on change
              }} 
              placeholder="Affiliate Code" 
            />
            {affiliateCodeError && (
              <div style={{ color: '#D63F15', fontSize: '12px', marginLeft: '4px' }}>
                {affiliateCodeError}
              </div>
            )}
          </div>
          <TextInput name="address" label="Address" value={agentObj.location} maxLength={LOCATION_MAX_LENGTH} onChange={(e) => { const v = e.target.value.slice(0, LOCATION_MAX_LENGTH); setAddress(v); setAgentObj({ ...agentObj, location: v }); }} placeholder="Address" />
          <div style={{ margin: "1rem 0" }}>
            <h4>Select Location</h4>
            <LoadScriptNext googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
            <GoogleMap
           mapContainerStyle={mapContainerStyle}
             center={selectedLocation} 
              zoom={DefaultZoom}
              onClick={handleMapClick}
            >
             <MarkerF position={selectedLocation} />
               </GoogleMap>
            </LoadScriptNext>
          </div>
          <TextInput  name="coords"  label="Coordinates"  value={agentObj.coords}  onChange={async (e) => {   
                const coordsValue = e.target.value;
                setAgentObj((prev) => ({ ...prev, coords: coordsValue }));
                
                const [lat, lng] = coordsValue.split(",").map((coord) => parseFloat(coord.trim()));
                if (!isNaN(lat) && !isNaN(lng)) {
                  setSelectedLocation({ lat, lng });
                  
                  // Fetch and update the address field
                  const resolvedAddress = await fetchAddress(lat, lng);
                  if (resolvedAddress) {
                    setAddress(resolvedAddress);
                    setAgentObj((prev) => ({ ...prev, location: resolvedAddress }));
                  }
                }
              }}
              placeholder="Coordinates (e.g., lat, lng)" 
            />

          <div className="double-column-form aligned-right margin-top no-divider">
            <button className='btn-primary' onClick={submitAgent}>
              {loading ? <CircularProgress size={22} style={{ color: "#fff" }} /> : isEditMode ? "Update" : "Add"}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AddAgent;
